import type { FilterFn, Row } from "@tanstack/react-table";

import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";

import type { AccountClientRow, AccountColumnLabels } from "../../types";

import { BodyTypo } from "../../Common/BodyTypo";
import { HeaderTypo } from "../../Common/HeaderTypo";
import { createBackofficeFieldDecorators } from "../../utils/redactedFieldsHelper";
const columnHelper = createColumnHelper<AccountClientRow>();

export const rebateAccountFilter: FilterFn<AccountClientRow> = (
  row,
  columnId,
  value,
) => {
  if (!value) return true;
  if (columnId !== "rebateAccountID") return true;

  const rebateAccountID = row.original.rebateAccountID;
  const selectedIDs: string[] = value;

  return selectedIDs.includes(rebateAccountID);
};

export const sortByName = (
  rowA: Row<AccountClientRow>,
  rowB: Row<AccountClientRow>,
) => {
  return rowA.original.name.localeCompare(rowB.original.name);
};

export const clientAccountColumns = ({
  translatedContent,
  redactedFields = [],
  isBackoffice = false,
  showOfferIdColumn = false,
}: {
  translatedContent?: AccountColumnLabels;
  language: string;
  redactedFields?: string[];
  isBackoffice?: boolean;
  showOfferIdColumn?: boolean;
}) => {
  const { hiddenColumns, decorateTitle, getRedactedClassName } =
    createBackofficeFieldDecorators(redactedFields, isBackoffice);
  const columns = [
    columnHelper.accessor("accountID", {
      header: () => <HeaderTypo text={translatedContent?.acc} />,
      cell: (info) => <BodyTypo text={info.getValue()} />,
      sortingFn: "alphanumeric",
      sortDescFirst: false,
      size: 150,
    }),
    columnHelper.accessor("executionType", {
      header: () => <HeaderTypo text={translatedContent?.accType} />,
      cell: (info) => <BodyTypo text={info.getValue()} />,
      enableGlobalFilter: false,
      sortingFn: "alphanumeric",
      size: 170,
    }),
    columnHelper.accessor("clientID", {
      header: () => (
        <HeaderTypo
          text={decorateTitle("clientID", translatedContent?.clientId)}
        />
      ),
      cell: (info) => <BodyTypo text={info.getValue()} />,
      sortingFn: "alphanumeric",
      sortDescFirst: false,
      enableGlobalFilter: true,
      size: 120,
      meta: { ...getRedactedClassName("clientId") },
    }),
    columnHelper.accessor("name", {
      header: () => (
        <HeaderTypo text={decorateTitle("name", translatedContent?.name)} />
      ),
      cell: (info) => <BodyTypo text={info.getValue()} />,
      sortingFn: sortByName,
      size: 190,
      meta: { ...getRedactedClassName("name") },
    }),
    columnHelper.accessor("email", {
      header: () => (
        <HeaderTypo text={decorateTitle("email", translatedContent?.email)} />
      ),
      cell: (info) => <BodyTypo text={info.getValue()} />,
      sortingFn: "alphanumeric",
      size: 180,
      meta: { ...getRedactedClassName("email") },
    }),
    columnHelper.accessor("firstTradedDate", {
      header: () => <HeaderTypo text={translatedContent?.firstTradedDate} />,
      cell: (info) => {
        const { firstTradedDate } = info.row.original;
        return (
          <BodyTypo
            text={
              firstTradedDate
                ? dayjs(firstTradedDate).format("DD MMM YYYY")
                : "-"
            }
          />
        );
      },
      enableGlobalFilter: false,
      sortingFn: "alphanumeric",
      size: 180,
    }),
    columnHelper.accessor("lastTradedDate", {
      header: () => <HeaderTypo text={translatedContent?.lastTradedDate} />,
      cell: (info) => {
        const { lastTradedDate } = info.row.original;
        return (
          <BodyTypo
            text={
              lastTradedDate ? dayjs(lastTradedDate).format("DD MMM YYYY") : "-"
            }
          />
        );
      },
      enableGlobalFilter: false,
      sortingFn: "alphanumeric",
      size: 180,
    }),
    columnHelper.accessor("hasOfferID", {
      header: () => <HeaderTypo text={translatedContent?.hasOfferID} />,
      cell: (info) => {
        const offerID = info.getValue();
        return offerID ? <BodyTypo text={`HOID ${offerID}`} /> : null;
      },
      enableGlobalFilter: false,
      sortingFn: "alphanumeric",
      size: 120,
    }),
    columnHelper.accessor("rebateAccountID", {
      header: () => <HeaderTypo text={translatedContent?.rebateAccount} />,
      cell: (info) => <BodyTypo text={info.getValue()} />,
      enableGlobalFilter: false,
      filterFn: rebateAccountFilter,
      enableColumnFilter: true,
      sortingFn: "alphanumeric",
      sortDescFirst: false,
      size: 180,
    }),
  ];
  return columns.filter((column) => {
    const columnKey = String(column.accessorKey).toLowerCase();

    if (columnKey === "hasofferid" && !showOfferIdColumn) {
      return false;
    }

    return !hiddenColumns.map((k) => k.toLowerCase()).includes(columnKey);
  });
};
